<template>
  <div v-if="this.hasData" class="cw-message"
    :class="{ 'isMe': this.messageIsFromMe(this.message), 'showFullScreen': this.showFullScreen, 'show-avatar': this.showAvatar, 'same-sender': this.sameSenderAsBefore, 'positionAllMessagesLeft': this.positionAllMessagesLeft }">
    <div class="cw-icon-holder" v-if="this.showAvatar">
      <TickAvatar v-if="!this.sameSenderAsBefore" class="icon" :sender="this.messageSenderInfo" />
    </div>
    <div class="cw-message-content">

      <div class="cw-message-text" v-if="this.messageHtml" v-html="this.messageHtml" :class="{ 'use-custom-padding': this.useCustomPadding }"></div>
      <div class="cw-attachments" v-if="this.hasAttachments">
        <Attachment :attachmentLink="attachment" v-for="(attachment, index) in this.attachments" :key="index" />
      </div>

      <div class="cw-reply-options" v-if="this.hasReplyOptions">
        <BaseReplyOption :data="replyOption" :index="index" :useCustomActionPadding="this.useCustomActionPadding" v-for="(replyOption, index) in this.replyOptions" :key="index" />
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import TickAvatar from "@/components/atoms/TickAvatar.ce.vue";
import { displayDate, displayDistanceToNowDate } from "@/helpers/dateHelper";
import useAppStore from "@/store/app";
import { ChatWidgetActionRequest, ReplyOptionsData } from "@/TickApi";
import { Component, Prop } from "vue-facing-decorator";
import { Vue } from "vue-facing-decorator";
import Attachment from "./Attachment.ce.vue";
import BaseReplyOption from "./ReplyOption/BaseReplyOption.ce.vue";


@Component({
  emits: [],
  components: { TickAvatar, BaseReplyOption, Attachment },
})
export default class Message extends Vue {
  @Prop() private message!: ChatWidgetActionRequest;
  @Prop() private sameSenderAsBefore!: boolean;
  @Prop() private showAvatar!: boolean;
  @Prop() private positionAllMessagesLeft!: boolean;
  @Prop() private useCustomInboundPadding!: boolean;
  @Prop() private useCustomOutboundPadding!: boolean;
  @Prop() private useCustomActionPadding!: boolean;

  private get showFullScreen(): boolean {
    return useAppStore().deviceShowFullScreen;
  }

  private get useCustomPadding(): boolean {
    return !this.messageIsFromMe
      ? this.useCustomOutboundPadding
      : this.useCustomInboundPadding;
  }

  private messageIsFromMe(message: ChatWidgetActionRequest) {
    return message.from == useAppStore().deviceAddress || message.from == "preview_me";
  }


  private get messageHtml() {
    return this.message.messageHtml || "";
  }

  private get hasData() {
    return this.message.messageHtml || this.hasReplyOptions || this.hasAttachments;
  }

  private get hasReplyOptions() {
    return this.replyOptions.length > 0;
  }

  private get replyOptions() {
    return this.message.replyOptions || [];
  }

  private get hasAttachments() {
    return this.attachments.length > 0;
  }

  private get attachments() {
    return this.message.attachmentLinks || [];
  }

  private get messageSenderInfo() {
    return this.message.senderInfo;
  }

  private get displayDate() {
    return displayDistanceToNowDate(this.message.sentDate);
  }

  private get tooltipDate() {
    return displayDate(this.message.sentDate);
  }
}
</script>
<style lang="scss">
@import "@/styles/theme";

.cw-message-content {
  &.isMe {
    .cw-message-text {
      a {
        color: var(--cw-conversation-message-inbound-fg-color);
        cursor: pointer;
      }
    }
  }

  &:not(.isMe) {
    .cw-message-text {
      a {
        color: var(--cw-conversation-message-outbound-fg-color);
        cursor: pointer;
      }
    }
  }
}




.cw-message {
  display: grid;
  gap: 5px;

  grid-template-columns: 40px 1fr;
  grid-template-areas: 'icon content'
    'icon attachments'
    'icon replyoptions';


  &.showFullScreen {
    grid-template-columns: 10vw 1fr;
    gap: 2vw;

    .cw-message-text {
      margin-top: 2vw;
      font-size: var(--cw-mobile-font-size);

    }
  }

  &:not(.show-avatar) {
    grid-template-columns: 1fr;
    grid-template-areas: 'content'
      'attachments'
      'replyoptions';
  }

  .cw-message-content {
    grid-area: content;
    justify-self: left;

    .cw-message-text {
      padding-top: clamp(10px, 1vmin, 20px);
      padding-right: clamp(10px, 2vmin, 20px);
      padding-bottom: clamp(10px, 1vmin, 20px);
      padding-left: clamp(10px, 2vmin, 20px);
      margin-top: 5px;
    }
  }

  &.isMe {
    .cw-message-content {
      font-family: var(--cw-conversation-message-inbound-font-family);
      font-size: var(--cw-conversation-message-inbound-font-size);
    }

    &:not(.positionAllMessagesLeft) {
      .cw-message-content {
        justify-self: left !important;
      }
    }

    .cw-message-text {
      background: var(--cw-conversation-message-inbound-bg-color);
      color: var(--cw-conversation-message-inbound-fg-color);
      border: solid var(--cw-conversation-message-inbound-border-width) var(--cw-conversation-message-inbound-border-color);
      border-radius: var(--cw-conversation-message-inbound-border-radius);
      box-shadow: var(--cw-conversation-message-inbound-drop-shadow);

      &.use-custom-padding {
        padding: var(--cw-conversation-message-inbound-padding);
      }
    }
  }

  &:not(.isMe) {
    .cw-message-content {
      font-family: var(--cw-conversation-message-outbound-font-family);
      font-size: var(--cw-conversation-message-outbound-font-size);
    }

    &:not(.positionAllMessagesLeft) {
      .cw-message-content {
        justify-self: right !important;
      }
    }



    .cw-message-text {
      background: var(--cw-conversation-message-outbound-bg-color);
      color: var(--cw-conversation-message-outbound-fg-color);
      border: solid var(--cw-conversation-message-outbound-border-width) var(--cw-conversation-message-outbound-border-color);
      border-radius: var(--cw-conversation-message-outbound-border-radius);
      box-shadow: var(--cw-conversation-message-outbound-drop-shadow);

      &.use-custom-padding {
        padding: var(--cw-conversation-message-outbound-padding);
      }
    }
  }


  .cw-icon-holder {
    grid-area: icon;
  }

  .cw-reply-options {
    grid-area: replyoptions;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 5px;
    margin-bottom: 30px;
  }

  .cw-attachments {
    grid-area: attachments;

    display: grid;
    gap: 10px;
    margin-top: 5px;

  }


}


// /* For Mobile */
// @media screen and (hover: none) and (orientation:portrait),
// screen and (max-width: 800px) {

//   .cw-message {
//     grid-template-columns: 10vw 1fr;
//     gap: 2vw;

//     .cw-message-text {
//       margin-top: 2vw;
//       font-size: var(--cw-mobile-font-size);

//     }


//   }
// }</style>